import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { brandKindCopy } from '../features/onboarding/brandKind';
import { useAccount } from '../features/onboarding/useAccount';

/** The first letter of a brand's name (a whole code point), for the rail. */
function brandInitial(name: string): string {
  const [first] = Array.from(name.trim());
  return first ?? '?';
}

/**
 * Which brand the shell is showing, and the way to another one.
 *
 * Sits at the top of the sidebar because every screen below it is scoped to
 * the active brand. Switching sets the active id and returns to the dashboard:
 * a planner chat or a review page carries ids that belong to the brand being
 * left. "Add a brand" opens the same setup flow as first run.
 *
 * A brand past the plan's allowance carries a Locked badge and can still be
 * picked: it opens read-only, with the shell's banner saying why. A brand the
 * user was invited to carries a Member tag: someone else owns it.
 *
 * Compact on a phone (the name and caret in the top bar, the menu full width
 * under it); on the tablet rail an initial-letter button opening the same menu.
 */
export function BrandSwitcher() {
  const { business, businesses, setActiveBusinessId } = useAccount();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setIsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  if (!business) return null;

  const copy = brandKindCopy(business.kind);

  function choose(businessId: string) {
    setIsOpen(false);
    if (businessId === business?.id) return;
    setActiveBusinessId(businessId);
    void navigate('/', { replace: true });
  }

  return (
    <div className="brand-switcher" ref={rootRef}>
      <button
        className="brand-switcher__current"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {/* The tablet rail's whole switcher; CSS hides it on other tiers. */}
        <span className="brand-switcher__initial" aria-hidden="true">
          {brandInitial(business.name)}
        </span>
        <span className="brand-switcher__name">{business.name}</span>
        <span className="brand-switcher__caret" aria-hidden="true">
          {isOpen ? '▴' : '▾'}
        </span>
        <span className="brand-switcher__kicker">
          {copy.tag}
          {business.role === 'member' ? ' · Member' : ''}
          {business.locked ? ' · Locked' : ''}
          {businesses.length > 1 ? ` · ${businesses.length} brands` : ''}
        </span>
      </button>

      {isOpen ? (
        <div className="brand-switcher__menu">
          <ul className="brand-switcher__list" role="listbox" aria-label="Your brands">
            {businesses.map((entry) => {
              const isActive = entry.id === business.id;
              return (
                <li key={entry.id}>
                  <button
                    className={
                      isActive
                        ? 'brand-switcher__option brand-switcher__option--active'
                        : 'brand-switcher__option'
                    }
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => choose(entry.id)}
                  >
                    <span className="brand-switcher__option-name">{entry.name}</span>
                    <span className="brand-switcher__option-tags">
                      {entry.locked ? <span className="badge badge--locked">Locked</span> : null}
                      {entry.role === 'member' ? (
                        <span className="badge badge--member">Member</span>
                      ) : null}
                      <span className="brand-switcher__option-kind">
                        {brandKindCopy(entry.kind).tag}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <Link className="brand-switcher__add" to="/brands/new" onClick={() => setIsOpen(false)}>
            + Add a brand
          </Link>
        </div>
      ) : null}
    </div>
  );
}
